import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProjetPublic, projetImageUrl, articleImageUrl, galerieImageUrl } from '../services/api';
import { FiMapPin, FiArrowLeft, FiArrowRight, FiX, FiMap, FiLayers, FiGrid, FiPhone, FiMail, FiHome, FiEye, FiUser, FiCheck } from 'react-icons/fi';
import logoCohamac from '../assets/logo-COHAMAC.png';
import iconeCohamac from '../assets/icone.png';

const defaultImg = 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&q=80';

const ProjetDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [lightboxSource, setLightboxSource] = useState<'projet' | 'galerie'>('projet');
  const [lightboxImages, setLightboxImages] = useState<any[]>([]);
  const [expandedGalerie, setExpandedGalerie] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!id) return;
    getProjetPublic(id)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const openLightbox = (index: number, imgs: any[], source: 'projet' | 'galerie') => {
    setLightboxImages(imgs);
    setLightboxSource(source);
    setLightbox(index);
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (lightbox === null || lightboxImages.length === 0) return;
    if (e.key === 'Escape') setLightbox(null);
    if (e.key === 'ArrowRight') setLightbox(prev => prev !== null ? (prev + 1) % lightboxImages.length : null);
    if (e.key === 'ArrowLeft') setLightbox(prev => prev !== null ? (prev - 1 + lightboxImages.length) % lightboxImages.length : null);
  }, [lightbox, lightboxImages]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (loading) return (
    <div className="pd2-loading">
      <div className="pd2-loading-spinner" />
      <p>Chargement du projet...</p>
    </div>
  );

  if (!data) return (
    <div className="pd2-loading">
      <p>Projet non trouvé</p>
      <Link to="/" className="lp2-btn-primary"><FiArrowLeft /> Retour à l'accueil</Link>
    </div>
  );

  const { projet, images, stats, articles, galeries = [] } = data;
  const mainImage = images.find((i: any) => i.is_main) || images[0];
  const heroImg = mainImage ? projetImageUrl(mainImage.filename) : defaultImg;
  const pct = stats.parcelles_total > 0 ? Math.round((stats.parcelles_attribuees / stats.parcelles_total) * 100) : 0;

  return (
    <div className="pd2">
      {/* ========== NAVBAR ========== */}
      <header className={`lp2-nav ${scrolled ? 'lp2-nav-scrolled' : ''}`}>
        <div className="lp2-nav-inner">
          <Link to="/" className="lp2-logo">
            <img src={logoCohamac} alt="COHAMAC" />
          </Link>
          <nav className="lp2-links pd2-nav-links-desktop">
            <Link to="/">Accueil</Link>
            <Link to="/#projets">Projets</Link>
            <Link to="/#contact">Contact</Link>
          </nav>
        </div>
      </header>

      {/* ========== HERO ========== */}
      <section className="pd2-hero" style={{ backgroundImage: `url(${heroImg})` }}>
        <div className="pd2-hero-overlay" />
        <div className="pd2-hero-content">
          <Link to="/" className="pd2-back">
            <FiArrowLeft /> Retour aux projets
          </Link>
          <div className="pd2-hero-badge">{projet.mode_acquisition || projet.reference || 'Projet'}</div>
          <h1>{projet.libelle}</h1>
          {projet.localisation && (
            <div className="pd2-hero-loc"><FiMapPin /> {projet.localisation}</div>
          )}
        </div>
      </section>

      {/* ========== INFO CARDS (floating bar) ========== */}
      <section className="pd2-info-bar">
        <div className="pd2-info-inner">
          {projet.superficie > 0 && (
            <div className="pd2-info-item">
              <div className="pd2-info-icon"><FiMap /></div>
              <div>
                <strong>{projet.superficie >= 10000 ? (projet.superficie / 10000) + ' ha' : projet.superficie + ' m²'}</strong>
                <span>Surface</span>
              </div>
            </div>
          )}
          {projet.titre_foncier && (
            <div className="pd2-info-item">
              <div className="pd2-info-icon"><FiLayers /></div>
              <div>
                <strong>{projet.titre_foncier}</strong>
                <span>Titre Foncier</span>
              </div>
            </div>
          )}
          {stats.parcelles_total > 0 && (
            <div className="pd2-info-item">
              <div className="pd2-info-icon"><FiGrid /></div>
              <div>
                <strong>{stats.parcelles_total}</strong>
                <span>Parcelles</span>
              </div>
            </div>
          )}
          {projet.mode_acquisition && (
            <div className="pd2-info-item">
              <div className="pd2-info-icon"><FiHome /></div>
              <div>
                <strong>{projet.mode_acquisition}</strong>
                <span>Acquisition</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ========== MAIN CONTENT ========== */}
      <div className="pd2-body">
        <div className="pd2-container">

          {/* Progress bar */}
          {stats.parcelles_total > 0 && (
            <div className="pd2-progress-card">
              <div className="pd2-progress-header">
                <h3>Progression des attributions</h3>
                <span className="pd2-progress-pct">{pct}%</span>
              </div>
              <div className="pd2-progress-bar">
                <div className="pd2-progress-fill" style={{ width: pct + '%' }} />
              </div>
              <div className="pd2-progress-label">
                <span>{stats.parcelles_attribuees} parcelles attribuées</span>
                <span>{stats.parcelles_total} total</span>
              </div>
            </div>
          )}

          {/* Description */}
          {projet.description && (
            <div className="pd2-section-card">
              <h2>À propos du projet</h2>
              <p className="pd2-description">{projet.description}</p>
            </div>
          )}

          {/* Project Images Gallery */}
          {images.length > 0 && (
            <div className="pd2-section-card">
              <h2>Photos du projet <span className="pd2-count">{images.length}</span></h2>
              <div className="pd2-gallery-grid">
                {images.map((img: any, i: number) => (
                  <div key={img.id} className="pd2-gallery-item" onClick={() => openLightbox(i, images, 'projet')}>
                    <img src={projetImageUrl(img.filename)} alt={img.caption || projet.libelle} />
                    <div className="pd2-gallery-hover">
                      <FiEye />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Galeries */}
          {galeries.length > 0 && (
            <div className="pd2-section-card">
              <h2>Galeries <span className="pd2-count">{galeries.length}</span></h2>
              <div className="pd2-galeries-list">
                {galeries.map((g: any) => {
                  const cover = g.images?.find((i: any) => i.is_cover) || g.images?.[0];
                  const isExpanded = expandedGalerie === g.id;
                  return (
                    <div key={g.id} className={`pd2-galerie-block ${isExpanded ? 'expanded' : ''}`}>
                      <div className="pd2-galerie-header" onClick={() => setExpandedGalerie(isExpanded ? null : g.id)}>
                        <div className="pd2-galerie-thumb">
                          {cover ? (
                            <img src={galerieImageUrl(cover.filename)} alt={g.titre} />
                          ) : (
                            <div className="pd2-galerie-no-thumb"><FiGrid /></div>
                          )}
                        </div>
                        <div className="pd2-galerie-info">
                          <h4>{g.titre}</h4>
                          {g.description && <p>{g.description}</p>}
                          <div className="pd2-galerie-meta">
                            {g.date_galerie && (
                              <span>{new Date(g.date_galerie).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                            )}
                            <span>{g.images?.length || 0} photo(s)</span>
                          </div>
                        </div>
                        <div className={`pd2-galerie-arrow ${isExpanded ? 'rotated' : ''}`}>
                          <FiArrowRight />
                        </div>
                      </div>
                      {isExpanded && g.images?.length > 0 && (
                        <div className="pd2-galerie-expanded">
                          <div className="pd2-gallery-grid">
                            {g.images.map((img: any, i: number) => (
                              <div key={img.id} className="pd2-gallery-item" onClick={() => openLightbox(i, g.images, 'galerie')}>
                                <img src={galerieImageUrl(img.filename)} alt={img.caption || g.titre} />
                                <div className="pd2-gallery-hover">
                                  <FiEye />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Related Articles */}
          {articles.length > 0 && (
            <div className="pd2-section-card">
              <h2>Actualités liées <span className="pd2-count">{articles.length}</span></h2>
              <div className="pd2-articles-grid">
                {articles.map((a: any) => (
                  <div key={a.id} className="pd2-article-card">
                    <div className="pd2-article-img">
                      <img src={a.imageprincipale ? articleImageUrl(a.imageprincipale) : defaultImg} alt={a.titre} />
                    </div>
                    <div className="pd2-article-body">
                      <div className="pd2-article-date">
                        {a.created ? new Date(a.created).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                      </div>
                      <h3>{a.titre}</h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ========== FOOTER ========== */}
      <footer className="lp2-footer">
        <div className="lp2-container">
          <div className="lp2-footer-grid">
            <div className="lp2-footer-brand">
              <img src={logoCohamac} alt="COHAMAC" className="lp2-footer-logo" />
              <p>La coopérative a pour but de contribuer au développement de l'immobilier et de l'habitat social au profit de ses membres.</p>
            </div>
            <div className="lp2-footer-col">
              <h4>Liens rapides</h4>
              <Link to="/">Accueil</Link>
              <Link to="/#cooperative">A propos</Link>
              <Link to="/#projets">Nos projets</Link>
              <Link to="/#bureau">Bureau</Link>
            </div>
            <div className="lp2-footer-col">
              <h4>Contact</h4>
              <span><FiPhone /> +221 77 556 42 15</span>
              <span><FiMapPin /> 31 Rue de Thiong, Dakar</span>
              <span><FiMail /> contact@cohamac.com</span>
            </div>
          </div>
          <div className="lp2-footer-bottom">
            <div className="lp2-footer-bottom-left">
              <img src={iconeCohamac} alt="" className="lp2-footer-icone" />
              <span>COHAMAC</span>
            </div>
            <span>Copyright COHAMAC {new Date().getFullYear()}. Tous droits réservés</span>
          </div>
        </div>
      </footer>

      {/* ========== LIGHTBOX ========== */}
      {lightbox !== null && lightboxImages.length > 0 && (
        <div className="pd2-lightbox" onClick={() => setLightbox(null)}>
          <button className="pd2-lb-close" onClick={() => setLightbox(null)}><FiX /></button>
          <button className="pd2-lb-prev" onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + lightboxImages.length) % lightboxImages.length); }}><FiArrowLeft /></button>
          <div className="pd2-lb-content" onClick={e => e.stopPropagation()}>
            <img
              src={lightboxSource === 'projet' ? projetImageUrl(lightboxImages[lightbox].filename) : galerieImageUrl(lightboxImages[lightbox].filename)}
              alt={lightboxImages[lightbox].caption || ''}
            />
            {lightboxImages[lightbox].caption && <div className="pd2-lb-caption">{lightboxImages[lightbox].caption}</div>}
            <div className="pd2-lb-counter">{lightbox + 1} / {lightboxImages.length}</div>
          </div>
          <button className="pd2-lb-next" onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % lightboxImages.length); }}><FiArrowRight /></button>
        </div>
      )}
    </div>
  );
};

export default ProjetDetail;
